import { useEffect, useRef } from "react";
import { Link } from "@/lib/router-compat";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/seo";
import { HOMEPAGE_H1_LINE_1, HOMEPAGE_H1_LINE_2, HOMEPAGE_LEDE } from "@/lib/homepageCopy";
import freedomHall from "@/assets/freedom-hall.webp";
import freedomHall1000Avif from "@/assets/freedom-hall-1000.avif";
import freedomHall640Avif from "@/assets/freedom-hall-640.avif";
import freedomHall1000Webp from "@/assets/freedom-hall-1000.webp";
import freedomHall640Webp from "@/assets/freedom-hall-640.webp";

const trustItems = [
  "Gold Standard STEP Award",
  "ABC Indiana/Kentucky Member",
  "Certified Energy Auditing Company",
  "Generac & Distech Partner",
  "100+ Years Combined Experience",
  "1-Year Workmanship Warranty",
];

const HomepageHero = () => {
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let ticking = false;
    const update = () => {
      const image = imageRef.current;
      if (image) {
        const travel = Math.min(window.scrollY * 0.08, 48);
        image.style.transform = `translate3d(0, ${travel}px, 0) scale(1.08)`;
      }
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <section data-testid="hero" className="relative flex min-h-[560px] items-center overflow-hidden [contain:paint] bg-charcoal text-charcoal-foreground md:h-[88vh]">
        <picture className="absolute -inset-y-[4%] inset-x-0 overflow-hidden">
          <source type="image/avif" media="(max-width: 767px)" srcSet={freedomHall640Avif} />
          <source type="image/avif" media="(min-width: 768px)" srcSet={freedomHall1000Avif} />
          <source type="image/webp" media="(max-width: 767px)" srcSet={freedomHall640Webp} />
          <source type="image/webp" media="(min-width: 768px)" srcSet={freedomHall1000Webp} />
          <img
            ref={imageRef}
            src={freedomHall}
            alt="Hadley Energy Solutions working at Freedom Hall Louisville"
            className="h-full w-full scale-[1.08] object-cover object-[58%_center] will-change-transform md:object-center"
            width="1000"
            height="750"
            fetchPriority="high"
            decoding="async"
          />
        </picture>
        <div className="absolute inset-0 gradient-overlay" aria-hidden="true" />

        <div className="container relative py-14 md:py-20">
          <div className="max-w-[920px]">
            <p className="mb-5 font-condensed text-[12px] font-bold uppercase tracking-[0.12em] text-primary-light md:text-[13px]">
              Commercial · Industrial · Residential HVAC
              <span className="hidden min-[480px]:inline"> · Since 2013</span>
            </p>
            <h1 className="hero-title max-w-[920px] text-[1.35rem] font-extrabold leading-[1.02] text-primary-foreground sm:text-[2.5rem] md:text-[4rem]">
              <span className="block whitespace-nowrap">{HOMEPAGE_H1_LINE_1}</span>
              {" "}
              <span className="block whitespace-nowrap">{HOMEPAGE_H1_LINE_2}</span>
            </h1>
            <p className="mt-6 max-w-[56ch] text-[17px] leading-[1.6] text-primary-foreground/85 md:text-[19px]">
              {HOMEPAGE_LEDE}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="h-[52px] rounded px-7 font-heading text-base font-bold">
                <Link to="/request-estimate">Request an Estimate</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-[52px] rounded border-primary-foreground/75 bg-transparent px-7 font-heading text-base font-bold text-primary-foreground hover:bg-primary-foreground hover:text-foreground">
                <a href={`tel:${COMPANY.phoneTel}`} data-cta="hero">
                  <Phone className="h-5 w-5" aria-hidden="true" /> Call {COMPANY.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section aria-label="Company credentials" className="bg-charcoal text-charcoal-foreground">
        <div className="mx-auto grid min-h-16 max-w-[1600px] grid-cols-2 items-stretch px-4 sm:grid-cols-3 lg:grid-cols-6 lg:px-8">
          {trustItems.map((item, index) => (
            <div
              key={item}
              className={`flex items-center justify-center px-3 py-3 text-center font-condensed text-[12px] font-bold uppercase leading-tight tracking-[0.12em] text-charcoal-foreground/80 lg:text-[13px] ${index > 0 ? "border-l border-charcoal-foreground/15" : ""}`}
            >
              {item}
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default HomepageHero;